import { useState } from 'react'
import { ConfirmDocumentRemovalDialog } from '../ConfirmDocumentRemovalDialog'
import { cardOfDocType } from '../../utils/documentAutofill/targets'
import { useDocumentAutofillContext } from './DocumentAutofillContext'
import { DocumentAutofillStatus } from './DocumentAutofillStatus'

interface CardAutofillStatusProps<T extends string, D extends { fileName: string }> {
    card: T
    /** The cards of this documents grid and their names, to name the card a wrong file belongs on. */
    types: readonly T[]
    labels: Record<T, string>
    /** The card's file with this id, stored or pending. */
    fileOf: (sourceId: string) => { stored: D } | { pending: File } | undefined
    onRemovePending: (file: File) => void
    /** Deletes a stored document; undefined where this grid can't. */
    onRemoveStored?: (document: D) => void
    removalDescription?: (document: D) => string
}

/**
 * A documents card's AI status line, wired to the card: names the card a wrong file belongs on, and takes such a file off this card
 * (a pending file at once, a stored document after the same confirmation as its chip's ✕). Nothing on a card the form does not read.
 */
export const CardAutofillStatus = <T extends string, D extends { fileName: string }>({
    card,
    types,
    labels,
    fileOf,
    onRemovePending,
    onRemoveStored,
    removalDescription,
}: CardAutofillStatusProps<T, D>) => {
    const autofill = useDocumentAutofillContext()
    const [confirmRemoval, setConfirmRemoval] = useState<{ document: D; sourceId: string } | null>(null)
    const label = autofill?.labelOf(card)
    if (!autofill || !label) return null

    return (
        <>
            <DocumentAutofillStatus
                status={autofill.statusOf(card)}
                label={label}
                cardName={labels[card]}
                cardNameOf={(docType) => {
                    const other = types.find((candidate) => candidate === cardOfDocType(docType))
                    return other ? labels[other] : undefined
                }}
                removeFileAction={(sourceId) => {
                    const file = fileOf(sourceId)
                    if (file && 'pending' in file)
                        return () => {
                            autofill.removedWrongFile(card, sourceId)
                            onRemovePending(file.pending)
                        }
                    if (file && onRemoveStored) return () => setConfirmRemoval({ document: file.stored, sourceId })
                    return undefined
                }}
                lockedMessage={autofill.lockedMessage}
                onCancel={() => autofill.cancel(card)}
                onRetry={() => autofill.retry(card)}
            />
            <ConfirmDocumentRemovalDialog
                fileName={confirmRemoval?.document.fileName ?? null}
                description={confirmRemoval ? removalDescription?.(confirmRemoval.document) : undefined}
                onClose={() => setConfirmRemoval(null)}
                onConfirm={() => {
                    if (confirmRemoval) {
                        autofill.removedWrongFile(card, confirmRemoval.sourceId)
                        onRemoveStored?.(confirmRemoval.document)
                    }
                    setConfirmRemoval(null)
                }}
            />
        </>
    )
}
